<template>
    <teleport to="#page-footer-slot" :disabled="!footerSlotExists">
        <div id="app-footer" class="p-2">
            <div class="flex flex-row items-center justify-between">
                <div class="flex items-center">
                    <slot name="left"></slot>
                </div>
                <div class="flex items-center">
                    <slot name="right"></slot>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, onUnmounted, nextTick } from 'vue';

const footerSlotExists = ref(false);
const mounted = ref(false);

// Set the global flag immediately to prevent timing issues
window.__pageFooterActive = true;

const checkFooterSlot = () => {
    if (!mounted.value) return;
    const slotElement = document.getElementById('page-footer-slot');
    footerSlotExists.value = !!slotElement;

    // Also ensure App.vue knows the footer is active
    if (slotElement) {
        window.__pageFooterActive = true;
    }
};

onMounted(async () => {
    mounted.value = true;

    // Wait for DOM to be ready
    await nextTick();

    // Check if the footer slot exists with timeout
    let attempts = 0;
    const maxAttempts = 200; // 10 seconds max (200 attempts × 50ms = 10s)

    const waitForSlot = () => {
        checkFooterSlot();

        if (footerSlotExists.value || attempts >= maxAttempts) {
            // Force trigger App.vue to check footer state immediately
            window.dispatchEvent(new CustomEvent('pageFooterChanged'));
            return;
        }

        attempts++;
        setTimeout(waitForSlot, 50);
    };

    waitForSlot();

    // Setup DOM mutation observer as backup
    if (!footerSlotExists.value) {
        setupMutationObserver();
    }

    // Additional fallback: keep checking periodically if teleport failed
    const fallbackCheck = setInterval(() => {
        if (!footerSlotExists.value && mounted.value) {
            checkFooterSlot();
            if (footerSlotExists.value) {
                window.dispatchEvent(new CustomEvent('pageFooterChanged'));
                clearInterval(fallbackCheck);
                mutationObserver?.disconnect();
            }
        } else if (footerSlotExists.value) {
            clearInterval(fallbackCheck);
            mutationObserver?.disconnect();
        }
    }, 1000);

    // Clean up fallback after 30 seconds
    setTimeout(() => {
        clearInterval(fallbackCheck);
        mutationObserver?.disconnect();
    }, 30000);
});

let mutationObserver = null;

const setupMutationObserver = () => {
    // Watch for DOM changes that might add the footer slot
    if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(() => {
            if (!footerSlotExists.value && mounted.value) {
                checkFooterSlot();
                if (footerSlotExists.value) {
                    window.dispatchEvent(new CustomEvent('pageFooterChanged'));
                    mutationObserver?.disconnect();
                }
            }
        });

        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false
        });
    }
};

onBeforeUnmount(() => {
    mounted.value = false;
    mutationObserver?.disconnect();
});

onUnmounted(() => {
    // Clear the global flag when component is unmounted
    window.__pageFooterActive = false;
    footerSlotExists.value = false;

    // Force trigger App.vue to check footer state immediately
    window.dispatchEvent(new CustomEvent('pageFooterChanged'));
});
</script>

<style scoped>
/* #app-footer {
    background-color: #f0f0f0;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
} */
</style>
